import ReactEChartsCore from 'echarts-for-react/lib/core';
import { echarts, BRAND_COLOR, baseChartOptions } from '../../shared/echarts-config';
import { ChartContainer } from '../../shared/ChartContainer';
import { EmptyState } from '../../shared/EmptyState';
import type { FpCountPoint } from '../../../../types/analytics.types';

type AdvisorRetentionChartProps = {
  points: FpCountPoint[];
};

const TITLE = 'Advisor Retention';
const SUBTITLE = 'Active advisors each month';

const buildChartOptions = (points: FpCountPoint[]) => ({
  ...baseChartOptions,
  tooltip: {
    trigger: 'axis' as const,
    formatter: (params: Array<{ dataIndex: number }>) => {
      const idx = params[0]?.dataIndex;
      if (idx == null) return '';
      const point = points[idx];
      if (!point) return '';
      const label = new Date(point.date).toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      });
      return (
        `<div style="font-weight:600;margin-bottom:6px">${label}</div>` +
        `<div style="display:flex;justify-content:space-between;gap:16px">` +
        `<span>Active Advisors</span><span style="font-weight:600">${point.count}</span></div>`
      );
    },
  },
  xAxis: {
    type: 'category' as const,
    data: points.map((p) => p.date),
    axisLabel: {
      formatter: (value: string) => {
        const d = new Date(value);
        return `${d.getUTCMonth() + 1}/${String(d.getUTCFullYear()).slice(2)}`;
      },
      fontSize: 12,
      color: '#6b7280',
    },
    axisLine: { lineStyle: { color: '#e5e7eb' } },
  },
  yAxis: {
    type: 'value' as const,
    minInterval: 1,
    axisLabel: { fontSize: 12, color: '#6b7280' },
    splitLine: { lineStyle: { color: '#f3f4f6' } },
  },
  series: [
    {
      name: 'Active Advisors',
      type: 'line',
      data: points.map((p) => p.count),
      smooth: true,
      symbol: 'circle',
      symbolSize: 6,
      lineStyle: { width: 2.5, color: BRAND_COLOR },
      itemStyle: { color: BRAND_COLOR },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(61, 101, 96, 0.25)' },
            { offset: 1, color: 'rgba(61, 101, 96, 0.02)' },
          ],
        },
      },
    },
  ],
});

export const AdvisorRetentionChart = ({ points }: AdvisorRetentionChartProps) => {
  if (points.length === 0) {
    return (
      <ChartContainer title={TITLE} subtitle={SUBTITLE}>
        <EmptyState message="No advisor activity available for the selected date range." />
      </ChartContainer>
    );
  }

  return (
    <ChartContainer title={TITLE} subtitle={SUBTITLE}>
      <ReactEChartsCore
        echarts={echarts}
        option={buildChartOptions(points)}
        style={{ height: 300 }}
        notMerge
        lazyUpdate
      />
    </ChartContainer>
  );
};
